import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Hammer, Wrench, Quote } from "lucide-react";
import SEO from "@/components/SEO";
import RichContent from "@/components/RichContent";
import publicApi from "@/lib/publicApi";
import usePageContent, { s } from "@/hooks/usePageContent";
import { localePath, parseLocaleFromPath } from "@/lib/locale";
import { LocalBusinessLD } from "@/components/StructuredData";
import useSiteConfig from "@/hooks/useSiteConfig";

const INSTRUMENT_SLUGS = {
  prim: "primovi",
  bisernica: "bisernice",
  brac: "bracevi",
  bugarija: "bugarije",
  celo: "cela",
  berda: "berde",
};

const HERO_IMAGE_SRC = "/seed/workshop_mainpage_hero.webp";

export default function Home() {
  const { t } = useTranslation();
  const location = useLocation();
  const { locale } = parseLocaleFromPath(location.pathname);
  const { data: page } = usePageContent("home", locale);
  const { data: storyPage } = usePageContent("story", locale);
  const [products, setProducts] = useState([]);
  const siteConfig = useSiteConfig();
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    publicApi
      .get("/public/product-types", { params: { locale } })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]));
  }, [locale]);

  return (
    <>
      <SEO
        title={page?.meta_title}
        description={page?.meta_description}
        image={`${origin}${HERO_IMAGE_SRC}`}
      />
      <Helmet>
        <link rel="preload" as="image" href={HERO_IMAGE_SRC} />
      </Helmet>
      <LocalBusinessLD origin={origin} settings={siteConfig} heroImageUrl={`${origin}${HERO_IMAGE_SRC}`} />
      {/* HERO — Ken Burns still image + optional video swap slot (admin-controlled). */}
      <section className="relative overflow-hidden bg-tz-espresso text-tz-cream" data-testid="home-hero">
        <div className="absolute inset-0 overflow-hidden">
          {siteConfig?.hero_video_url ? (
            <video
              src={siteConfig.hero_video_url}
              poster={HERO_IMAGE_SRC}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              aria-hidden="true"
              data-testid="home-hero-video"
              className="w-full h-full object-cover opacity-55"
            />
          ) : (
            <img
              src={HERO_IMAGE_SRC}
              alt=""
              loading="eager"
              data-testid="home-hero-image"
              className="w-full h-full object-cover opacity-55 tz-kenburns"
            />
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-tr from-tz-espresso via-tz-espresso/85 to-tz-espresso/40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-24 lg:py-36">
          <div className="max-w-3xl animate-fade-up">
            <div className="text-[11px] uppercase tracking-[0.3em] text-primary/90 mb-6">
              {s(page, "hero_eyebrow")}
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight" data-testid="home-hero-title">
              {s(page, "hero_title")}
            </h1>
            <div className="mt-8 wood-divider w-32 bg-primary/40" />
            <p className="mt-8 text-lg sm:text-xl text-tz-cream/80 leading-relaxed max-w-2xl">
              {s(page, "hero_subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to={localePath(locale, "/models")}
                data-testid="home-cta-models"
                className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-5 py-3 text-sm font-medium shadow-interactive hover:bg-primary/90 transition-colors"
              >
                {s(page, "hero_cta_primary")}
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
              <Link
                to={localePath(locale, "/story")}
                data-testid="home-cta-story"
                className="inline-flex items-center gap-2 rounded-lg border border-tz-cream/30 px-5 py-3 text-sm font-medium hover:bg-white/5 transition-colors"
              >
                {s(page, "hero_cta_secondary")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-background grain-bg">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-start">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-4">{s(page, "intro_eyebrow")}</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-foreground leading-tight">
              {s(page, "intro_title")}
            </h2>
          </div>
          <div className="space-y-5 text-foreground/80 text-lg leading-relaxed" data-testid="home-intro-body">
            <RichContent html={s(page, "intro_p1")} />
            <RichContent html={s(page, "intro_p2")} />
          </div>
        </div>
      </section>

      {/* MODELS */}
      <section className="bg-card border-y border-border/60">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-3">{s(page, "models_eyebrow")}</div>
              <h2 className="font-serif text-4xl lg:text-5xl text-foreground">{s(page, "models_title")}</h2>
              <p className="mt-3 text-foreground/70 max-w-xl">{s(page, "models_subtitle")}</p>
            </div>
            <Link to={localePath(locale, "/models")} className="hidden sm:inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
              {t("actions.view_all")} <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-models-grid">
            {products.map((p) => (
              <Link
                key={p.id}
                to={localePath(locale, `/${INSTRUMENT_SLUGS[p.id] || p.slug_path || p.id}`)}
                data-testid={`product-card-${p.id}`}
                className="group block bg-background rounded-lg border border-border/60 overflow-hidden shadow-ambient hover:shadow-interactive transition-shadow"
              >
                <div className="aspect-[5/4] overflow-hidden bg-muted">
                  <img
                    src={p.hero_image_url}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-baseline justify-between">
                    <h3 className="font-serif text-2xl text-foreground">{p.name}</h3>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{p.tagline}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-xs text-primary">
                    {t("actions.see_more")} <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* STORY + RESTORATION */}
      <section className="bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-2 gap-10">
          <Link
            to={localePath(locale, "/story")}
            data-testid="home-story-teaser"
            className="group block bg-tz-espresso text-tz-cream rounded-lg overflow-hidden shadow-ambient hover:shadow-interactive transition-shadow"
          >
            <div className="p-8 lg:p-10">
              <div className="flex items-center gap-2 text-primary text-[11px] uppercase tracking-[0.25em] mb-4">
                <Hammer className="h-3.5 w-3.5" strokeWidth={1.5} />
                {s(page, "story_teaser_eyebrow")}
              </div>
              <h3 className="font-serif text-3xl lg:text-4xl mb-4">{s(page, "story_teaser_title")}</h3>
              <RichContent html={s(page, "story_teaser_body")} className="text-tz-cream/75 leading-relaxed" />
              <div className="mt-6 inline-flex items-center gap-1.5 text-sm text-primary group-hover:gap-3 transition-all">
                {s(page, "story_teaser_cta")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </div>
            </div>
          </Link>

          <Link
            to={localePath(locale, "/restorations")}
            data-testid="home-restoration-teaser"
            className="group block relative rounded-lg overflow-hidden shadow-ambient hover:shadow-interactive transition-shadow min-h-[280px]"
          >
            <img src="/seed/restoration_after.webp" alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-tr from-tz-espresso/90 via-tz-espresso/60 to-transparent" />
            <div className="relative p-8 lg:p-10 h-full flex flex-col justify-end text-tz-cream">
              <div className="flex items-center gap-2 text-primary text-[11px] uppercase tracking-[0.25em] mb-3">
                <Wrench className="h-3.5 w-3.5" strokeWidth={1.5} />
                Restauracija
              </div>
              <h3 className="font-serif text-3xl lg:text-4xl mb-3">{s(page, "restoration_teaser_title")}</h3>
              <RichContent html={s(page, "restoration_teaser_body")} className="text-tz-cream/85 leading-relaxed max-w-md" />
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm text-primary group-hover:gap-3 transition-all">
                {t("actions.see_more")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* QUOTE (from story page content) */}
      <section className="bg-tz-espresso text-tz-cream">
        <div className="max-w-4xl mx-auto px-5 lg:px-10 py-20 text-center">
          <Quote className="h-8 w-8 text-primary mx-auto mb-6" strokeWidth={1} />
          <blockquote className="font-serif text-3xl lg:text-4xl italic leading-snug">
            &ldquo;{s(storyPage, "quote")}&rdquo;
          </blockquote>
          <div className="mt-6 text-sm text-tz-cream/60 uppercase tracking-[0.25em]">
            — {s(storyPage, "quote_attrib")}
          </div>
        </div>
      </section>
    </>
  );
}
